import React, { useCallback, useEffect } from "react";
import styles from "./index.module.scss";
import classnames from "classnames";
import { ScrollPositionType, ScrollLabel } from "constants/scrollPositionType";
import { EventBusType } from "events/EventBus";
import { EventBus } from "events/EventBus";
import { ReaderToolsEvent } from "events/EventTypes";
import { useStore, StoreTypes } from "context";
import { ScrollPositionButtonStateType } from "constants/scrollPositionType";
import { ScrollCoordinate } from "constants/scrollPositionType";
import * as types from "constants/actionTypes";
import { ModalComponentType } from "components/common/Modal/ModalComponentGenerator";
import { ExtendedContentType, getPaginationConfig } from "../ExtendedContentContainer";

const buttonListData = [
  {
    label: ScrollLabel.Next,
    value: ScrollPositionType.Next,
    stopSign: ScrollPositionButtonStateType.Bottom,
  },
  {
    label: ScrollLabel.Prev,
    value: ScrollPositionType.Prev,
    stopSign: ScrollPositionButtonStateType.Head,
  },
];

export const ExtendedContentScrollPositionButton = ({ extendedContentType }) => {
  const [{ scrollPositionButtonState, currentPage, totalPages, shouldSplited }, extendedContentDispatch] = useStore(StoreTypes.extendedContent);

  // 根據 currentPage 和 totalPages 計算並更新按鈕狀態
  useEffect(() => {
    // 該類型自行管理按鈕狀態，不在此覆蓋
    const { selfManagedPagination } = getPaginationConfig(extendedContentType);
    if (selfManagedPagination) return;

    let buttonState;
    if (totalPages <= 1) {
      buttonState = ScrollPositionButtonStateType.None;
    } else if (currentPage === 0) {
      buttonState = ScrollPositionButtonStateType.Head;
    } else if (currentPage === totalPages - 1) {
      buttonState = ScrollPositionButtonStateType.Bottom;
    } else {
      buttonState = ScrollPositionButtonStateType.Middle;
    }

    // 只在狀態實際改變時才更新
    if (buttonState !== scrollPositionButtonState) {
      extendedContentDispatch({
        type: types.SET_EXTENDED_CONTENT_SCROLL_POSITION_BUTTON_STATE,
        payload: { scrollPositionButtonState: buttonState },
      });
    }
  }, [currentPage, totalPages, scrollPositionButtonState, extendedContentDispatch, extendedContentType]);

  const changePopWindowScrollPositionHandler = useCallback((scrollPosition) => {

    EventBus.emit({
      eventBusType: EventBusType.ExtendedContent,
      event: ReaderToolsEvent.ChangePopWindowScrollPositionEvent,
      payload: { scrollPosition },
    });

    // ScrollPositionType.None 是 onMouseUp 時傳入，不需要處理
    if (scrollPosition === ScrollPositionType.None) {
      EventBus.emit({
        eventBusType: EventBusType.ExtendedContent,
        event: ReaderToolsEvent.ChangePopWindowScrollPositionEvent,
        payload: { scrollPosition },
      });
      return;
    }

    EventBus.emit({
      eventBusType: EventBusType.ExtendedContent,
      event: ReaderToolsEvent.ChangePopWindowScrollPositionEvent,
      payload: { scrollPosition },
    });

    if (scrollPosition === ScrollPositionType.Next) {
      const nextPage = Math.min(currentPage + 1, Math.max(totalPages - 1, 0));
      if (nextPage !== currentPage) {
        extendedContentDispatch({
          type: types.SET_EXTENDED_CONTENT_CURRENT_PAGE,
          payload: { currentPage: nextPage },
        });
      }
    } else if (scrollPosition === ScrollPositionType.Prev) {
      
      const prevPage = Math.max(currentPage - 1, 0);
      if (prevPage !== currentPage) {
        extendedContentDispatch({
          type: types.SET_EXTENDED_CONTENT_CURRENT_PAGE,
          payload: { currentPage: prevPage },
        });
      }
    }
  }, [currentPage, totalPages, extendedContentDispatch]);

  const { showPageInfo } = getPaginationConfig(extendedContentType);

  return (
    <div className={styles.extendedContentScrollPositionButton}>
      {buttonListData.filter(b => {return (extendedContentType === ModalComponentType.WordGrid
      || extendedContentType === ModalComponentType.Hearing
      || extendedContentType === ExtendedContentType.WordGridSubContent) ? b.value === ScrollPositionType.Next : b.value === ScrollPositionType.Prev}).map((item) => {
        const disableCheck =
          scrollPositionButtonState === item.stopSign ||
          scrollPositionButtonState === ScrollPositionButtonStateType.None;
        return (
          <div
            className={classnames(styles.button, {
              [styles.disabled]: disableCheck,
            })}
            style={{ backgroundColor: disableCheck && "#999" }}
            key={item.value}
            onMouseDown={() => {
              if (disableCheck) return;
              return changePopWindowScrollPositionHandler(item.value);
            }}
            onMouseUp={() =>
              changePopWindowScrollPositionHandler(ScrollPositionType.None)
            }
          >
            {item.label}
          </div>
        );
      })}
      {showPageInfo && <div className={styles.pageInfo} aria-label={`page ${currentPage + 1} of ${totalPages}`}>
        {`${currentPage + 1}/${totalPages}`}
      </div>}
      {buttonListData.filter(b => {return (extendedContentType === ModalComponentType.WordGrid
      || extendedContentType === ModalComponentType.Hearing
      || extendedContentType === ExtendedContentType.WordGridSubContent) ? b.value === ScrollPositionType.Prev : b.value === ScrollPositionType.Next}).map((item) => {
        const disableCheck =
          scrollPositionButtonState === item.stopSign ||
          scrollPositionButtonState === ScrollPositionButtonStateType.None;
        return (
          <div
            className={classnames(styles.button, {
              [styles.disabled]: disableCheck,
            })}
            style={{ backgroundColor: disableCheck && "#999" }}
            key={item.value}
            onMouseDown={() => {
              if (disableCheck) return;
              return changePopWindowScrollPositionHandler(item.value);
            }}
            onMouseUp={() =>
              changePopWindowScrollPositionHandler(ScrollPositionType.None)
            }
          >
            {item.label}
          </div>
        );
      })}
    </div>
  );
};

// 記錄目前位置與上次位置，判斷是否擦掉劃記與按鈕顏色
export const scrollPositionButtonBehavior = ({
  currentScroll,
  lastScroll,
  coordinate,
}) => {
  const eventBusType = EventBusType.ExtendedContent;
  if (coordinate === ScrollCoordinate.X) {
    if (currentScroll !== lastScroll && (currentScroll < 0 || lastScroll < 0)) {
      EventBus.emit({
        eventBusType,
        event: ReaderToolsEvent.ClickEraseAllEvent,
      });
    }
  } else if (coordinate === ScrollCoordinate.Y) {
    if (currentScroll !== lastScroll && (currentScroll > 0 || lastScroll > 0)) {
      EventBus.emit({
        eventBusType,
        event: ReaderToolsEvent.ClickEraseAllEvent,
      });
    }
  }
};

export const ScrollButtonStateBehavior = (state) => {
  const eventBusType = EventBusType.ExtendedContent;
  if (state === ScrollPositionButtonStateType.Head) {
    EventBus.emit({
      eventBusType,
      event: ReaderToolsEvent.ChangePopWindowScrollPositionButtonStateEvent,
      payload: {
        scrollPositionButtonState: ScrollPositionButtonStateType.Head,
      },
    });
  } else if (state === ScrollPositionButtonStateType.Bottom) {
    EventBus.emit({
      eventBusType,
      event: ReaderToolsEvent.ChangePopWindowScrollPositionButtonStateEvent,
      payload: {
        scrollPositionButtonState: ScrollPositionButtonStateType.Bottom,
      },
    });
  } else if (state === ScrollPositionButtonStateType.None) {
    EventBus.emit({
      eventBusType,
      event: ReaderToolsEvent.ChangePopWindowScrollPositionButtonStateEvent,
      payload: {
        scrollPositionButtonState: ScrollPositionButtonStateType.None,
      },
    });
  } else {
    EventBus.emit({
      eventBusType,
      event: ReaderToolsEvent.ChangePopWindowScrollPositionButtonStateEvent,
      payload: {
        scrollPositionButtonState: ScrollPositionButtonStateType.Middle,
      },
    });
  }
};
